/**
 * Store dédié au dashboard client (sidebar enrichie + dashboard détaillé).
 * Séparé de clientStore pour éviter les re-renders croisés.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import api from '../utils/api';
import { handleApiError, handleApiSuccess } from '../utils/errorHandler';
import { logger } from '../utils/logger';
import type {
  ClientSidebarItem,
  ClientDashboardResponse,
  MissionBlock,
  BlockType,
  ViewMode,
  MissionKanbanColumn,
} from '../types/clientDashboard';

interface ClientDashboardStore {
  sidebar: ClientSidebarItem[];
  isLoadingSidebar: boolean;
  currentDashboard: ClientDashboardResponse | null;
  isLoadingDashboard: boolean;

  // Mode standalone (utilisé depuis MissionDetailPage qui n'a pas de currentDashboard).
  // Quand renseigné, les mutations bloc updatent CE state en plus de currentDashboard.
  currentMissionId: string | null;
  currentMissionBlocks: MissionBlock[];
  currentMissionViewMode: ViewMode;
  currentMissionKanbanColumns: MissionKanbanColumn[];

  fetchSidebar: () => Promise<void>;
  fetchDashboard: (clientId: string) => Promise<void>;
  fetchMissionBlocks: (missionId: string) => Promise<void>;
  clearMissionBlocks: () => void;

  // Client mutations
  toggleClientHidden: (clientId: string) => Promise<void>;
  setClientColor: (clientId: string, color: string | null) => Promise<void>;
  setClientCategory: (clientId: string, category: string | null) => Promise<void>;
  setClientPosition: (clientId: string, position: number) => Promise<void>;

  // Block mutations
  createBlock: (missionId: string, data: Partial<MissionBlock> & { type: BlockType }) => Promise<MissionBlock | null>;
  updateBlock: (blockId: string, data: Partial<MissionBlock>) => Promise<MissionBlock | null>;
  deleteBlock: (blockId: string) => Promise<void>;
  reorderBlocks: (missionId: string, blocks: { id: string; position: number; layoutX: number; layoutY: number; layoutW: number; layoutH: number }[]) => Promise<void>;

  // Kanban mutations
  setViewMode: (missionId: string, mode: ViewMode) => Promise<void>;
  createKanbanColumn: (missionId: string, name: string, color?: string | null) => Promise<MissionKanbanColumn | null>;
  updateKanbanColumn: (colId: string, data: Partial<Pick<MissionKanbanColumn, 'name' | 'color' | 'position'>>) => Promise<void>;
  deleteKanbanColumn: (colId: string) => Promise<void>;
  reorderKanbanColumns: (missionId: string, columns: { id: string; position: number }[]) => Promise<void>;
  moveBlockToColumn: (missionId: string, blockId: string, kanbanColumnId: string, kanbanPosition: number) => Promise<void>;
}

export const useClientDashboardStore = create<ClientDashboardStore>((set, get) => ({
  sidebar: [],
  isLoadingSidebar: false,
  currentDashboard: null,
  isLoadingDashboard: false,
  currentMissionId: null,
  currentMissionBlocks: [],
  currentMissionViewMode: 'canvas',
  currentMissionKanbanColumns: [],

  fetchMissionBlocks: async (missionId) => {
    try {
      const res = await api.get(`/missions/${missionId}/blocks`);
      // Backend renvoie maintenant { viewMode, kanbanColumns, blocks } ; tolérer ancien format (array brut).
      const data = res.data;
      if (Array.isArray(data)) {
        set({ currentMissionId: missionId, currentMissionBlocks: data, currentMissionViewMode: 'canvas', currentMissionKanbanColumns: [] });
      } else {
        set({
          currentMissionId: missionId,
          currentMissionBlocks: data.blocks || [],
          currentMissionViewMode: (data.viewMode as ViewMode) || 'canvas',
          currentMissionKanbanColumns: data.kanbanColumns || [],
        });
      }
    } catch (e: any) {
      handleApiError(e, 'le chargement des blocs');
      set({ currentMissionId: missionId, currentMissionBlocks: [], currentMissionViewMode: 'canvas', currentMissionKanbanColumns: [] });
    }
  },

  clearMissionBlocks: () =>
    set({ currentMissionId: null, currentMissionBlocks: [], currentMissionViewMode: 'canvas', currentMissionKanbanColumns: [] }),

  fetchSidebar: async () => {
    if (!get().sidebar.length) set({ isLoadingSidebar: true });
    try {
      const res = await api.get('/clients/sidebar');
      set({ sidebar: res.data, isLoadingSidebar: false });
      logger.log('✅ Sidebar clients:', res.data.length);
    } catch (e: any) {
      handleApiError(e, 'le chargement de la sidebar clients');
      set({ isLoadingSidebar: false });
    }
  },

  fetchDashboard: async (clientId) => {
    set({ isLoadingDashboard: true });
    try {
      const res = await api.get(`/clients/${clientId}/dashboard`);
      set({ currentDashboard: res.data, isLoadingDashboard: false });
    } catch (e: any) {
      handleApiError(e, 'le chargement du dashboard client');
      set({ isLoadingDashboard: false });
    }
  },

  toggleClientHidden: async (clientId) => {
    const current = get().sidebar.find(c => c.id === clientId);
    if (!current) return;
    const newHidden = !current.isHidden;
    // Optimistic update
    set(state => ({
      sidebar: state.sidebar.map(c => c.id === clientId ? { ...c, isHidden: newHidden } : c)
    }));
    try {
      await api.put(`/clients/${clientId}`, { isHidden: newHidden });
      handleApiSuccess(newHidden ? 'Client masqué' : 'Client réaffiché');
    } catch (e: any) {
      // Revert
      set(state => ({
        sidebar: state.sidebar.map(c => c.id === clientId ? { ...c, isHidden: current.isHidden } : c)
      }));
      handleApiError(e, 'le changement de visibilité');
    }
  },

  setClientColor: async (clientId, color) => {
    const previous = get().sidebar.find(c => c.id === clientId)?.color ?? null;
    set(state => ({
      sidebar: state.sidebar.map(c => c.id === clientId ? { ...c, color } : c)
    }));
    try {
      await api.put(`/clients/${clientId}`, { color });
    } catch (e: any) {
      set(state => ({
        sidebar: state.sidebar.map(c => c.id === clientId ? { ...c, color: previous } : c)
      }));
      handleApiError(e, 'le changement de couleur');
    }
  },

  setClientCategory: async (clientId, category) => {
    const previous = get().sidebar.find(c => c.id === clientId)?.category ?? null;
    set(state => ({
      sidebar: state.sidebar.map(c => c.id === clientId ? { ...c, category } : c)
    }));
    try {
      await api.put(`/clients/${clientId}`, { category });
    } catch (e: any) {
      set(state => ({
        sidebar: state.sidebar.map(c => c.id === clientId ? { ...c, category: previous } : c)
      }));
      handleApiError(e, 'le changement de catégorie');
    }
  },

  setClientPosition: async (clientId, position) => {
    set(state => ({
      sidebar: state.sidebar.map(c => c.id === clientId ? { ...c, position } : c)
    }));
    try {
      await api.put(`/clients/${clientId}`, { position });
    } catch (e: any) {
      handleApiError(e, 'le réordonnancement');
    }
  },

  createBlock: async (missionId, data) => {
    try {
      const res = await api.post(`/missions/${missionId}/blocks`, data);
      const block: MissionBlock = res.data;
      // Optimistic add to current dashboard ET au state standalone.
      set(state => {
        const next: Partial<ClientDashboardStore> = {};
        if (state.currentDashboard) {
          next.currentDashboard = {
            ...state.currentDashboard,
            client: {
              ...state.currentDashboard.client,
              missions: state.currentDashboard.client.missions.map(m =>
                m.id === missionId
                  ? { ...m, blocks: [...(m.blocks || []), block] }
                  : m
              )
            }
          };
        }
        if (state.currentMissionId === missionId) {
          next.currentMissionBlocks = [...state.currentMissionBlocks, block];
        }
        return next as any;
      });
      return block;
    } catch (e: any) {
      handleApiError(e, 'la création du bloc');
      return null;
    }
  },

  updateBlock: async (blockId, data) => {
    try {
      const res = await api.patch(`/missions/blocks/${blockId}`, data);
      const updated: MissionBlock = res.data;
      set(state => {
        const next: Partial<ClientDashboardStore> = {};
        if (state.currentDashboard) {
          next.currentDashboard = {
            ...state.currentDashboard,
            client: {
              ...state.currentDashboard.client,
              missions: state.currentDashboard.client.missions.map(m => ({
                ...m,
                blocks: m.blocks?.map(b => b.id === blockId ? { ...b, ...updated } : b)
              }))
            }
          };
        }
        if (state.currentMissionBlocks.some(b => b.id === blockId)) {
          next.currentMissionBlocks = state.currentMissionBlocks.map(b =>
            b.id === blockId ? { ...b, ...updated } : b
          );
        }
        return next as any;
      });
      return updated;
    } catch (e: any) {
      handleApiError(e, 'la mise à jour du bloc');
      return null;
    }
  },

  deleteBlock: async (blockId) => {
    // Optimistic
    set(state => {
      const next: Partial<ClientDashboardStore> = {
        currentMissionBlocks: state.currentMissionBlocks.filter(b => b.id !== blockId),
      };
      if (state.currentDashboard) {
        next.currentDashboard = {
          ...state.currentDashboard,
          client: {
            ...state.currentDashboard.client,
            missions: state.currentDashboard.client.missions.map(m => ({
              ...m,
              blocks: m.blocks?.filter(b => b.id !== blockId)
            }))
          }
        };
      }
      return next as any;
    });
    try {
      await api.delete(`/missions/blocks/${blockId}`);
    } catch (e: any) {
      handleApiError(e, 'la suppression du bloc');
      // Refetch pour resync
      const cd = get().currentDashboard;
      const mid = get().currentMissionId;
      if (cd) get().fetchDashboard(cd.client.id);
      if (mid) get().fetchMissionBlocks(mid);
    }
  },

  reorderBlocks: async (missionId, blocks) => {
    try {
      await api.patch(`/missions/${missionId}/blocks/reorder`, { blocks });
      // Optimistic local sur les deux states.
      set(state => {
        const byId = new Map(blocks.map(b => [b.id, b]));
        const next: Partial<ClientDashboardStore> = {};
        if (state.currentDashboard) {
          next.currentDashboard = {
            ...state.currentDashboard,
            client: {
              ...state.currentDashboard.client,
              missions: state.currentDashboard.client.missions.map(m => {
                if (m.id !== missionId) return m;
                return {
                  ...m,
                  blocks: m.blocks?.map(b => {
                    const upd = byId.get(b.id);
                    return upd ? { ...b, ...upd } : b;
                  })
                };
              })
            }
          };
        }
        if (state.currentMissionId === missionId) {
          const reordered = [...state.currentMissionBlocks]
            .map(b => {
              const upd = byId.get(b.id);
              return upd ? { ...b, ...upd } : b;
            })
            .sort((a, b) => a.position - b.position);
          next.currentMissionBlocks = reordered;
        }
        return next as any;
      });
    } catch (e: any) {
      handleApiError(e, 'le réordonnancement des blocs');
    }
  },

  // === Kanban ===

  setViewMode: async (missionId, mode) => {
    // Optimistic
    set(state => state.currentMissionId === missionId ? { currentMissionViewMode: mode } : state);
    try {
      await api.patch(`/missions/${missionId}/view-mode`, { viewMode: mode });
    } catch (e: any) {
      handleApiError(e, 'le changement de mode');
      // Revert
      get().fetchMissionBlocks(missionId);
    }
  },

  createKanbanColumn: async (missionId, name, color = null) => {
    try {
      const res = await api.post(`/missions/${missionId}/kanban-columns`, { name, color });
      const col: MissionKanbanColumn = res.data;
      set(state => state.currentMissionId === missionId
        ? { currentMissionKanbanColumns: [...state.currentMissionKanbanColumns, col] }
        : state);
      return col;
    } catch (e: any) {
      handleApiError(e, 'la création de la colonne');
      return null;
    }
  },

  updateKanbanColumn: async (colId, data) => {
    // Optimistic
    set(state => ({
      currentMissionKanbanColumns: state.currentMissionKanbanColumns.map(c =>
        c.id === colId ? { ...c, ...data } : c
      ),
    }));
    try {
      await api.patch(`/missions/kanban-columns/${colId}`, data);
    } catch (e: any) {
      handleApiError(e, 'la modification de la colonne');
      const mid = get().currentMissionId;
      if (mid) get().fetchMissionBlocks(mid);
    }
  },

  deleteKanbanColumn: async (colId) => {
    const fallbackId = (() => {
      const cols = [...get().currentMissionKanbanColumns].sort((a, b) => a.position - b.position);
      const col = cols.find(c => c.id === colId);
      if (!col) return null;
      const others = cols.filter(c => c.id !== colId);
      return others[0]?.id || null;
    })();
    if (!fallbackId) {
      handleApiError(new Error('Impossible de supprimer la dernière colonne'), 'la suppression');
      return;
    }
    // Optimistic : déplacer les blocs vers la colonne fallback, supprimer la colonne
    set(state => ({
      currentMissionKanbanColumns: state.currentMissionKanbanColumns.filter(c => c.id !== colId),
      currentMissionBlocks: state.currentMissionBlocks.map(b =>
        b.kanbanColumnId === colId ? { ...b, kanbanColumnId: fallbackId } : b
      ),
    }));
    try {
      await api.delete(`/missions/kanban-columns/${colId}`);
    } catch (e: any) {
      handleApiError(e, 'la suppression de la colonne');
      const mid = get().currentMissionId;
      if (mid) get().fetchMissionBlocks(mid);
    }
  },

  reorderKanbanColumns: async (missionId, columns) => {
    // Optimistic
    set(state => ({
      currentMissionKanbanColumns: state.currentMissionKanbanColumns
        .map(c => {
          const upd = columns.find(x => x.id === c.id);
          return upd ? { ...c, position: upd.position } : c;
        })
        .sort((a, b) => a.position - b.position),
    }));
    try {
      await api.patch(`/missions/${missionId}/kanban-columns/reorder`, { columns });
    } catch (e: any) {
      handleApiError(e, 'le réordonnancement');
      get().fetchMissionBlocks(missionId);
    }
  },

  moveBlockToColumn: async (missionId, blockId, kanbanColumnId, kanbanPosition) => {
    // Optimistic
    set(state => ({
      currentMissionBlocks: state.currentMissionBlocks.map(b =>
        b.id === blockId ? { ...b, kanbanColumnId, kanbanPosition } : b
      ),
    }));
    try {
      await api.patch(`/missions/${missionId}/kanban-blocks/move`, { blockId, kanbanColumnId, kanbanPosition });
    } catch (e: any) {
      handleApiError(e, 'le déplacement du bloc');
      get().fetchMissionBlocks(missionId);
    }
  },
}));

export { useShallow };
